import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import TopBar from "./TopBar";
import Sidebar from "./Sidebar";
import { SecondarySidebar } from "./SecondarySidebar";
import { SecondarySubmenuLinks } from "./SecondarySubmenuLinks";
import { GestionFacturationPanel, GestionFacturesAchatPanel } from "./GestionSecondaryPanels";
import { gestionItems } from "./nav/GestionMenu";
import { comptaMainItems } from "./nav/ComptaMenu";
import {
  SECONDARY_TITLES,
  itemsForSecondary,
  type SecondaryPanelKind,
} from "./nav/secondarySidebarNav";
import { PRIMARY_SIDEBAR_WIDTH_PX, SECONDARY_SIDEBAR_WIDTH_PX } from "./layoutConstants";
import type { User } from "@supabase/supabase-js";
import { supabase } from "../../lib/supabase";
import { getProfile, getOnboardingDraft } from "../../lib/profiles";
import {
  fetchClientDossiers,
  fetchDossiers,
  fetchDossiersMembre,
  createDossier,
  hasCabinet,
  isCabinetOwner,
} from "../../lib/dossiers";
import { useDossierStore } from "../../store/dossierStore";
import { useParametresStore } from "../../store/parametresStore";
import { useMembreAccess } from "../../hooks/useMembreAccess";
import { cn } from "@/lib/utils";
import { ParametresOverlayNav } from "./nav/ParametresOverlayNav";

// ─── Types ───────────────────────────────────────────────────────
interface LayoutProps {
  children: React.ReactNode;
}

/** Chemins qui appartiennent exclusivement au mode Compta (pas de partage avec Gestion). */
function isExclusiveComptaPath(path: string): boolean {
  return path.startsWith("/compta/");
}

/** Chemins partagés : accessibles depuis Gestion OU Compta selon le contexte de navigation. */
function isSharedPath(path: string): boolean {
  return (
    path.startsWith("/transactions") ||
    path.startsWith("/gestion/transactions") ||
    path.startsWith("/gestion/factures-vente") ||
    path.startsWith("/gestion/produits/import") ||
    path === "/export/operations" ||
    path === "/bilan"
  );
}

function isGestionOnlyPath(path: string): boolean {
  return path === "/dashboard" || path.startsWith("/cabinet") || path.startsWith("/parametres") || path === "/compte" || path.startsWith("/compte/");
}


// ─── Component ───────────────────────────────────────────────────
const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [secondaryPanel, setSecondaryPanel] = useState<SecondaryPanelKind | null>(null);
  const [activeMainTab, setActiveMainTab] = useState<"gestion" | "compta">("gestion");
  const [notifications] = useState(3);
  const [user, setUser] = useState<User | null>(null);
  const [clientDossiers, setClientDossiers] = useState<
    { id: string; nom: string; regime_fiscal?: string | null; type_comptabilite?: "tresorerie" | "engagement" | null }[]
  >([]);
  const [cabinetDossiers, setCabinetDossiers] = useState<
    { id: string; nom: string; regime_fiscal?: string | null; type_comptabilite?: "tresorerie" | "engagement" | null }[]
  >([]);
  const [isCabinetUser, setIsCabinetUser] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [profileStructure, setProfileStructure] = useState<"grosse" | "petite" | "independant" | null>(null);
  const [accessToast, setAccessToast] = useState<string | null>(null);
  const { activeDossier, setActiveDossier } = useDossierStore();
  const typeComptabilite = activeDossier?.type_comptabilite ?? "engagement";
  const isTresorerie = typeComptabilite === "tresorerie";

  const { loadParametres } = useParametresStore();
  const { acces_gestion, acces_compta, loading: accessLoading, role: membreRole } = useMembreAccess();
  const isGrandeEntrepriseComptaEngagement = profileStructure === "grosse";

  useEffect(() => {
    console.log("[AUTH] redirect vers (Layout):", location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (activeDossier?.id) loadParametres(activeDossier.id);
  }, [activeDossier?.id]);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => {
      console.log("[AUTH] Layout getUser initial:", data.user?.id, data.user?.email);
      setUser(data.user);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("[AUTH] Layout onAuthStateChange:", event, session?.user?.id);
      // Ne pas mettre user à null sur événements transitoires sans session (évite perte d’état / effets en chaîne).
      if (event === "SIGNED_OUT") {
        setUser(null);
        return;
      }
      if (session?.user) {
        setUser(session.user);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    hasCabinet().then(setIsCabinetUser);
    isCabinetOwner().then(setIsOwner);
  }, [user?.email]);

  useEffect(() => {
    if (!user?.id) {
      setProfileStructure(null);
      return;
    }
    getProfile(user.id)
      .then((p) => {
        const fromProfile = p?.structure ?? null;
        if (fromProfile) return setProfileStructure(fromProfile);
        const draft = getOnboardingDraft();
        setProfileStructure(draft?.structure ?? null);
      })
      .catch(() => setProfileStructure(null));
  }, [user?.id]);

  // Charger client (dossiers_users) + cabinet + membres (dossier_membres) et fusionner pour les collaborateurs
  useEffect(() => {
    if (!user?.id) return;

    console.log("[Layout] Chargement dossiers - user:", user?.email);

    const mergeUniqueById = (
      lists: {
        id: string;
        nom: string;
        regime_fiscal?: string | null;
        type_comptabilite?: "tresorerie" | "engagement" | null;
      }[][]
    ) => {
      const m = new Map<
        string,
        { id: string; nom: string; regime_fiscal?: string | null; type_comptabilite?: "tresorerie" | "engagement" | null }
      >();
      for (const list of lists) {
        for (const d of list) {
          if (d?.id && !m.has(d.id)) {
            m.set(d.id, {
              id: d.id,
              nom: d.nom,
              regime_fiscal: d.regime_fiscal ?? null,
              type_comptabilite: d.type_comptabilite ?? null,
            });
          }
        }
      }
      return [...m.values()].sort((a, b) => (a.nom || "").localeCompare(b.nom || ""));
    };

    const safeFetch = async <T,>(label: string, fn: () => Promise<T>, fallback: T): Promise<T> => {
      try {
        return await fn();
      } catch (e) {
        console.error("[Layout] Échec", label, e);
        return fallback;
      }
    };

    void (async () => {
      const client = await safeFetch("fetchClientDossiers", () => fetchClientDossiers(), []);
      const cabinet = await safeFetch("fetchDossiers", () => fetchDossiers(), []);
      const dossiersResult = await safeFetch("fetchDossiersMembre", () => fetchDossiersMembre(), []);

      console.log("[Layout] Dossiers membre:", dossiersResult);

      const clientList = mergeUniqueById([
        client.map((d) => ({
          id: d.id,
          nom: d.nom,
          regime_fiscal: d.regime_fiscal ?? null,
          type_comptabilite: d.type_comptabilite ?? null,
        })),
        dossiersResult,
      ]);
      const cabinetList = mergeUniqueById([
        cabinet.map((d) => ({
          id: d.id,
          nom: d.nom,
          regime_fiscal: d.regime_fiscal ?? null,
          type_comptabilite: d.type_comptabilite ?? null,
        })),
        dossiersResult,
      ]);

      setClientDossiers(clientList);
      setCabinetDossiers(cabinetList);

      const mergedCount = mergeUniqueById([clientList, cabinetList]).length;
      console.log("[AUTH] dossiers chargés (fusion client∪cabinet):", mergedCount);

      let list = cabinet.length > 0 ? cabinetList : clientList;
      // Collaborateur : cabinet & client vides mais lignes dans dossier_membres
      if (list.length === 0 && dossiersResult.length > 0) {
        list = dossiersResult;
      }

      if (list.length > 0) {
        console.log(
          "[Layout] Dossiers trouvés:",
          list.map((d) => ({ id: d.id, nom: d.nom }))
        );

        const current = useDossierStore.getState().activeDossier;
        const inList =
          current?.id != null && list.some((d) => d.id === current.id);
        let final: {
          id: string;
          nom: string;
          regime_fiscal?: string | null;
          type_comptabilite?: "tresorerie" | "engagement" | null;
        };

        if (inList) {
          final = list.find((d) => d.id === current!.id)!;
          if (
            final.nom !== current!.nom ||
            final.regime_fiscal !== current!.regime_fiscal ||
            final.type_comptabilite !== current!.type_comptabilite
          ) {
            setActiveDossier(final);
          }
        } else {
          final = list[0];
          setActiveDossier(final);
        }
        console.log("[Layout] Dossier actif final:", final.id, final.nom);
        return;
      }
      // Aucun dossier : créer automatiquement le premier uniquement pour un propriétaire de cabinet.
      // Un collaborateur invité ne doit jamais déclencher la création d'un dossier vide.
      const ownsCabinet = await isCabinetOwner().catch(() => false);
      if (!ownsCabinet) {
        console.log("[Layout] Aucun dossier accessible et utilisateur non propriétaire: pas de création auto.");
        return;
      }

      // Propriétaire sans dossier : on peut créer un premier dossier depuis le brouillon onboarding.
      const draft = getOnboardingDraft();
      const nom = draft?.entreprise?.trim() || (await getProfile(user.id))?.entreprise?.trim();
      if (nom) {
        try {
          const d = await createDossier(nom);
          setCabinetDossiers([
            { id: d.id, nom: d.nom, regime_fiscal: d.regime_fiscal ?? null, type_comptabilite: d.type_comptabilite ?? null },
          ]);
          setClientDossiers((prev) =>
            prev.some((x) => x.id === d.id)
              ? prev
              : [{ id: d.id, nom: d.nom, regime_fiscal: d.regime_fiscal ?? null, type_comptabilite: d.type_comptabilite ?? null }, ...prev]
          );
          setActiveDossier({
            id: d.id,
            nom: d.nom,
            regime_fiscal: d.regime_fiscal ?? null,
            type_comptabilite: d.type_comptabilite ?? null,
          });
          console.log("[Layout] Dossier actif final:", d.id, d.nom);
        } catch (_) {
          // Ignorer les erreurs (ex. RLS)
        }
      }
    })();
  }, [user?.id]);

  // Sync de secours si activeDossier devient null alors qu'on a des dossiers
  useEffect(() => {
    const list = cabinetDossiers.length > 0 ? cabinetDossiers : clientDossiers;
    if (list.length > 0 && !activeDossier) setActiveDossier(list[0]);
  }, [clientDossiers, cabinetDossiers, activeDossier, setActiveDossier]);

  const isComptaMode = activeMainTab === "compta";
  const isOnParametresSection = location.pathname.startsWith("/parametres");

  // Sync activeMainTab : navigation indépendante Gestion vs Compta
  const navFrom = (location.state as { navFrom?: "gestion" | "compta" } | null)?.navFrom;
  useEffect(() => {
    const path = location.pathname;
    if (isExclusiveComptaPath(path)) {
      setActiveMainTab("compta");
    } else if (isSharedPath(path)) {
      if (navFrom === "compta") {
        setActiveMainTab("compta");
      } else {
        setActiveMainTab("gestion");
      }
    } else if (isGestionOnlyPath(path) || path.startsWith("/gestion/")) {
      setActiveMainTab("gestion");
    }
  }, [location.pathname, location.state, navFrom]);

  const isEntreprise = profileStructure === "grosse" || profileStructure === "petite" || profileStructure === "independant";
  const isCabinetFirm = isCabinetUser && !!user?.user_metadata?.cabinet_name;

  const isGestionModulePath = (path: string) => {
    return (
      isGestionOnlyPath(path) ||
      path.startsWith("/gestion/") ||
      path.startsWith("/transactions")
    );
  };

  useEffect(() => {
    if (!isTresorerie) return;
    if (activeMainTab !== "compta") return;
    setActiveMainTab("gestion");
    setSecondaryPanel(null);
    if (location.pathname.startsWith("/compta/")) {
      navigate(isCabinetFirm ? "/cabinet" : "/dashboard");
    }
  }, [activeMainTab, isCabinetFirm, isTresorerie, location.pathname, navigate]);

  // Guard global : un dossier en trésorerie ne doit jamais accéder aux routes /compta/*
  useEffect(() => {
    if (!isTresorerie) return;
    if (!location.pathname.startsWith("/compta/")) return;
    setActiveMainTab("gestion");
    setSecondaryPanel(null);
    navigate("/dashboard", { replace: true });
  }, [isTresorerie, location.pathname, navigate]);

  // Si l'utilisateur n'a accès qu'à un seul mode, forcer ce mode
  useEffect(() => {
    if (accessLoading) return;

    if (acces_gestion && !acces_compta) {
      if (activeMainTab === "compta") {
        setActiveMainTab("gestion");
        navigate(isCabinetFirm ? "/cabinet" : "/dashboard");
      }
    } else if (!acces_gestion && acces_compta) {
      if (activeMainTab === "gestion") {
        setActiveMainTab("compta");
        navigate("/compta/saisie");
      }
    }
  }, [acces_gestion, acces_compta, accessLoading, activeMainTab, isCabinetFirm, navigate, setActiveMainTab]);

  const showParametresEntrepriseNav =
    !(Boolean(activeDossier?.id) && accessLoading) &&
    (membreRole === "owner" || membreRole === null);

  useEffect(() => {
    if (!showParametresEntrepriseNav) {
      setSecondaryPanel((p) => (p === "parametres" ? null : p));
      return;
    }
    if (isOnParametresSection) {
      setActiveMainTab("gestion");
    }
  }, [isOnParametresSection, showParametresEntrepriseNav]);

  useEffect(() => {
    if (!location.pathname.startsWith("/parametres")) {
      setSecondaryPanel((p) => (p === "parametres" ? null : p));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (accessLoading) return;
    // Appliquer le blocage URL seulement quand on a identifié un membre
    // (sinon, risque de redirection prématurée pendant l'hydratation).
    if (!membreRole) return;
    const path = location.pathname;
    const comptaBlocked = isExclusiveComptaPath(path) && !acces_compta;
    const gestionBlocked = isGestionModulePath(path) && !acces_gestion;
    if (!comptaBlocked && !gestionBlocked) return;

    console.log("[AUTH] Layout garde accès module:", {
      path,
      membreRole,
      acces_gestion,
      acces_compta,
      comptaBlocked,
      gestionBlocked,
    });

    setAccessToast("Accès non autorisé");

    // Ne jamais envoyer vers "/" (landing publique) : la session reste active mais l’utilisateur croit être déconnecté.
    if (gestionBlocked && acces_compta) {
      console.log("[AUTH] redirection garde → /compta/revision/balance (Compta autorisée)");
      navigate("/compta/revision/balance", { replace: true });
      return;
    }
    if (comptaBlocked && acces_gestion) {
      console.log("[AUTH] redirection garde → /dashboard (Gestion autorisée)");
      navigate("/dashboard", { replace: true });
      return;
    }
    if (acces_gestion) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (acces_compta) {
      navigate("/compta/revision/balance", { replace: true });
      return;
    }
    console.warn("[AUTH] membre sans accès gestion ni compta — redirection /dashboard avec toast");
    navigate("/dashboard", { replace: true });
  }, [accessLoading, acces_compta, acces_gestion, location.pathname, navigate, membreRole]);

  useEffect(() => {
    if (!accessToast) return;
    const t = window.setTimeout(() => setAccessToast(null), 2500);
    return () => window.clearTimeout(t);
  }, [accessToast]);

  const currentNavItems = isComptaMode
    ? comptaMainItems
    : gestionItems
        .filter((i) => i.path !== "/parametres/entreprise" || showParametresEntrepriseNav)
        .map((i) => {
          let path = i.path;
          if (i.path === "/dashboard") path = isCabinetFirm ? "/cabinet" : "/dashboard";
          return { ...i, path };
        });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
    window.location.reload();
  };

  const allNavPaths: { path: string; label: string; matchPrefix?: boolean }[] = [
    ...currentNavItems.map((i) => ({ path: i.path, label: i.label })),
    { path: "/cabinet/organisation", label: "Organisation du cabinet" },
    { path: "/export/operations", label: "Exporter les opérations" },
    { path: "/compta/factures-achat", label: "Factures fournisseurs" },
    { path: "/compta/factures-vente", label: "Factures clients" },
    { path: "/compta/transactions", label: "Transactions bancaires" },
    { path: "/gestion/factures-achat", label: "Factures d'achat", matchPrefix: true },
    { path: "/gestion/factures-vente", label: "Factures clients", matchPrefix: true },
    { path: "/gestion/factures-vente/nouvelle", label: "Créer une facture" },
    { path: "/gestion/factures-vente/import", label: "Importer factures de vente" },
    { path: "/gestion/factures-vente/importees", label: "Factures importées" },
    { path: "/gestion/factures-vente/valider", label: "Valider import facture", matchPrefix: true },
    { path: "/gestion/devis", label: "Devis" },
    { path: "/gestion/devis/nouveau", label: "Nouveau devis" },
    { path: "/gestion/produits", label: "Produits" },
    { path: "/gestion/produits/import", label: "Imports" },
    { path: "/gestion/clients", label: "Clients" },
    { path: "/gestion/fournisseurs", label: "Fournisseurs" },
    { path: "/transactions", label: "Transactions bancaires", matchPrefix: true },
    { path: "/gestion/transactions/saisie", label: "Saisie des opérations" },
    { path: "/compta/journaux", label: "Journaux" },
    { path: "/compta/revision/feuilles", label: "Feuilles de travail" },
    { path: "/compta/dossier-revision", label: "Dossier de clôture", matchPrefix: true },
    { path: "/compte", label: "Gérer mon compte" },
    { path: "/compte/informations", label: "Gérer mon compte" },
    { path: "/compte/mot-de-passe", label: "Gérer mon compte" },
    { path: "/compte/connexions-externes", label: "Gérer mon compte" },
    { path: "/compta/dossier-client/ged", label: "GED — Dossier client" },
    { path: "/parametres/entreprise", label: "Paramètres d'entreprise" },
    { path: "/parametres/exercice", label: "Exercice comptable" },
    { path: "/aide", label: "Aide" },
  ];
  const currentPage = (() => {
    const exact = allNavPaths.find((i) => !i.matchPrefix && i.path === location.pathname);
    if (exact) return exact.label;
    const prefix = allNavPaths
      .filter((i) => i.matchPrefix && (location.pathname === i.path || location.pathname.startsWith(i.path + "/")))
      .sort((a, b) => b.path.length - a.path.length)[0];
    return prefix?.label ?? "Dashboard";
  })();

  const toggleSecondary = (kind: SecondaryPanelKind) => {
    setSecondaryPanel((p) => (p === kind ? null : kind));
  };

  const secondaryOpen = secondaryPanel !== null;
  const secondaryNavItems = secondaryPanel ? itemsForSecondary(secondaryPanel) : null;
  const secondaryMeta = secondaryPanel ? SECONDARY_TITLES[secondaryPanel] : { title: "", subtitle: "" };

  return (
    <div className="flex h-screen overflow-hidden bg-[#f5f7fa] font-sans">
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          text-decoration: none;
          margin-bottom: 3px;
          border: 1px solid transparent;
          transform: translateX(0);
        }
        .nav-item:hover {
          background: rgba(255,255,255,0.3);
          transform: translateX(4px);
        }
        .nav-item.active {
          background: rgba(255,255,255,0.5);
          color: #9a3412;
          box-shadow: inset 2px 0 0 #ea580c;
          transform: translateX(0);
        }
        .nav-item.active .nav-label,
        .nav-item.active .nav-icon {
          color: #9a3412 !important;
        }
        .nav-item.active .nav-label {
          font-weight: 700;
        }
        .nav-icon {
          font-size: 1rem;
          width: 20px;
          text-align: center;
          flex-shrink: 0;
          color: #c2410c;
          transition: color 0.2s;
        }
        .nav-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #c2410c;
          white-space: nowrap;
          transition: color 0.2s;
        }

        .header-btn {
          background: white;
          border: 1px solid #E8E0D5;
          border-radius: 10px;
          padding: 8px 12px;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: 'IBM Plex Sans', system-ui, sans-serif;
        }
        .header-btn:hover {
          border-color: #F97316;
          box-shadow: 0 2px 12px rgba(249,115,22,0.1);
        }

        .search-input {
          background: white;
          border: 1px solid #E8E0D5;
          border-radius: 10px;
          padding: 9px 16px 9px 38px;
          font-size: 0.85rem;
          color: #3A2A1A;
          outline: none;
          font-family: 'IBM Plex Sans', system-ui, sans-serif;
          width: 260px;
          transition: all 0.2s;
        }
        .search-input:focus {
          border-color: #F97316;
          box-shadow: 0 0 0 3px rgba(249,115,22,0.08);
        }
        .search-input::placeholder { color: #C4B8A8; }

        .notif-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          width: 16px;
          height: 16px;
          background: linear-gradient(135deg, #F97316, #EAB308);
          border-radius: 50%;
          font-size: 0.6rem;
          font-weight: 700;
          color: #0A0A0A;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid #F2EDE6;
        }

        .sidebar-section-label {
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(180, 83, 9, 0.92);
          padding: 0 16px;
          margin: 16px 0 6px;
        }

        .collapse-btn {
          background: rgba(255,255,255,0.55);
          border: 1px solid rgba(154, 52, 18, 0.18);
          border-radius: 12px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s;
          font-size: 0.7rem;
          color: #9a3412;
        }
        .collapse-btn:hover {
          background: rgba(255,255,255,0.75);
          border-color: rgba(234, 88, 12, 0.35);
          color: #c2410c;
        }

        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

        .sidebar-nav-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .sidebar-nav-scroll::-webkit-scrollbar {
          width: 0;
          height: 0;
          display: none;
        }
        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes secondaryMenuItemIn {
          from { opacity: 0; transform: translateY(6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <Sidebar
        activeMainTab={activeMainTab}
        setActiveMainTab={setActiveMainTab}
        secondaryPanel={secondaryPanel}
        setSecondaryPanel={setSecondaryPanel}
        toggleSecondary={toggleSecondary}
        isCabinetFirm={isCabinetFirm}
        isOwner={isOwner}
        user={user}
        onLogout={handleLogout}
        accesGestion={acces_gestion}
        accesCompta={acces_compta}
        typeComptabilite={typeComptabilite}
        showParametresEntrepriseNav={showParametresEntrepriseNav}
      />

      <div
        role="presentation"
        onClick={() => setSecondaryPanel(null)}
        className={[
          "fixed bottom-0 right-0 top-0 z-[60] transition-[left] duration-300 ease-in-out",
          secondaryOpen ? "pointer-events-auto bg-slate-900/15" : "pointer-events-none bg-transparent",
        ].join(" ")}
        style={{
          left: secondaryOpen ? PRIMARY_SIDEBAR_WIDTH_PX + SECONDARY_SIDEBAR_WIDTH_PX : "100vw",
        }}
        aria-hidden={!secondaryOpen}
      />

      <SecondarySidebar
        isOpen={secondaryOpen}
        onClose={() => setSecondaryPanel(null)}
        title={secondaryMeta.title}
        subtitle={secondaryMeta.subtitle}
        contentClassName={
          secondaryPanel === "parametres"
            ? "flex min-h-0 flex-1 flex-col overflow-hidden p-0"
            : undefined
        }
      >
        {secondaryPanel === "parametres" ? (
          <ParametresOverlayNav />
        ) : secondaryPanel === "facturation" ? (
          <GestionFacturationPanel onNavigate={() => setSecondaryPanel(null)} />
        ) : secondaryPanel === "factures-achat" ? (
          <GestionFacturesAchatPanel onNavigate={() => setSecondaryPanel(null)} />
        ) : secondaryNavItems ? (
          <SecondarySubmenuLinks items={secondaryNavItems} onNavigate={() => setSecondaryPanel(null)} />
        ) : null}
      </SecondarySidebar>

      {/* ── MAIN ── */}
      <main
        className="flex min-h-0 flex-1 flex-col overflow-hidden transition-[margin-left] duration-300 ease-in-out"
        style={{
          marginLeft: `${PRIMARY_SIDEBAR_WIDTH_PX + (secondaryOpen ? SECONDARY_SIDEBAR_WIDTH_PX : 0)}px`,
        }}
      >
        <TopBar
          currentPage={currentPage}
          isGrandeEntrepriseComptaEngagement={isGrandeEntrepriseComptaEngagement}
          user={user}
          clientDossiers={clientDossiers}
          cabinetDossiers={cabinetDossiers}
          notifications={notifications}
          onLogout={handleLogout}
        />

        {/* ── CONTENT ── */}
        <div
          className={cn(
            "flex-1 overflow-y-auto p-6",
            isExclusiveComptaPath(location.pathname) ? "bg-slate-100" : "bg-[#f5f7fa]"
          )}
        >
          {children}
        </div>
      </main>
      {accessToast ? (
        <div className="fixed bottom-5 right-5 z-[200] rounded-lg bg-night px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm">
          {accessToast}
        </div>
      ) : null}
    </div>
  );
};

export default Layout;